import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EnvelopeSimple, CheckCircle, SpinnerGap, ShieldCheck } from '@phosphor-icons/react';
import { requestMagicLink } from '../services/customerApi';

const CustomerLoginPage = () => {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Ingresa tu correo');
            return;
        }
        setLoading(true);
        try {
            await requestMagicLink(email.trim());
            setSent(true);
        } catch (err) {
            if (err.response?.status === 429) {
                toast.error('Demasiados intentos. Espera una hora antes de volver a intentar.');
            } else {
                toast.error('No se pudo procesar tu solicitud');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F4ECE0] flex items-center justify-center px-4 py-8" data-testid="customer-login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] mb-2">
                        Mis certificados
                    </h1>
                    <p className="text-sm text-[#4B5878]">Accede a tus certificados de viaje</p>
                </div>

                <div className="bg-white border border-[#ECE3D2] rounded-2xl p-6 sm:p-8 shadow-sm">
                    {!sent ? (
                        <form onSubmit={submit} className="space-y-5">
                            <div className="text-center space-y-2">
                                <div className="inline-flex w-12 h-12 rounded-full bg-[#FB7185]/10 items-center justify-center mb-2">
                                    <EnvelopeSimple size={22} weight="duotone" className="text-[#FB7185]" />
                                </div>
                                <h2 className="font-display text-xl font-semibold text-[#1F2D4B]">Inicia sesión sin contraseña</h2>
                                <p className="text-xs text-[#8B8474] leading-relaxed">
                                    Te enviaremos un enlace seguro a tu correo. Funciona en cualquier dispositivo y la sesión dura 30 días.
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-[#4B5878]">Tu correo electrónico</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@correo.com"
                                    autoFocus
                                    data-testid="customer-email-input"
                                    className="w-full h-12 px-4 rounded-md border border-[#ECE3D2] bg-white text-[#1F2D4B] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !email.trim()}
                                data-testid="send-magic-link-btn"
                                className="w-full h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <SpinnerGap size={18} className="animate-spin" /> Enviando…
                                    </>
                                ) : (
                                    'Enviarme el enlace'
                                )}
                            </button>

                            <div className="text-[11px] text-[#8B8474] flex items-start gap-2 pt-2 border-t border-[#ECE3D2]">
                                <ShieldCheck size={14} weight="duotone" className="text-[#2D7A52] flex-shrink-0 mt-0.5" />
                                <span>Tu información está protegida. Sólo enviamos el enlace si tu correo está vinculado a un certificado.</span>
                            </div>
                        </form>
                    ) : (
                        <div className="text-center space-y-4 py-4" data-testid="magic-link-sent">
                            <div className="inline-flex w-14 h-14 rounded-full bg-[#2D7A52]/10 items-center justify-center">
                                <CheckCircle size={26} weight="duotone" className="text-[#2D7A52]" />
                            </div>
                            <div>
                                <h2 className="font-display text-xl font-semibold text-[#1F2D4B] mb-1">Revisa tu correo</h2>
                                <p className="text-sm text-[#4B5878] leading-relaxed">
                                    Si <b>{email}</b> está registrado, te enviamos un enlace para entrar al portal.
                                </p>
                                <p className="text-xs text-[#8B8474] mt-3">
                                    El enlace expira en 24h. Revisa también tu carpeta de spam.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setSent(false); setEmail(''); }}
                                className="text-xs text-[#1F2D4B] underline hover:text-[#FB7185]"
                            >
                                Usar otro correo
                            </button>
                        </div>
                    )}
                </div>

                <p className="text-center text-[11px] text-[#8B8474] mt-6">
                    <Link to="/" className="hover:text-[#1F2D4B]">Volver al inicio</Link>
                </p>
            </div>
        </div>
    );
};

export default CustomerLoginPage;
